import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { 
  Shield, 
  ChevronLeft, 
  ChevronRight,
  Filter,
  User,
  Activity,
  Clock,
  FileText,
  AlertCircle,
  Eye,
  Loader2,
  Download,
  Calendar
} from 'lucide-react';
import { apiClient, AuditEvent, AuditEventStats } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import AuditEventDetails from '../components/AuditEventDetails';
import { cn } from '../lib/utils';
import { format } from 'date-fns';


const AuditLog: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [stats, setStats] = useState<AuditEventStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
  const [error, setError] = useState('');
  const [userPictures, setUserPictures] = useState<Record<string, string>>({});
  
  // Details popup state
  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  
  // Export state
  const [exporting, setExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportDateRange, setExportDateRange] = useState<{ from: Date; to: Date }>(() => {
    const now = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(now.getDate() - 7);
    sevenDaysAgo.setHours(0, 0, 0, 0);
    now.setHours(23, 59, 59, 999);
    return { from: sevenDaysAgo, to: now };
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalEvents, setTotalEvents] = useState(0);

  // Filter state
  const [filters, setFilters] = useState({
    userEmail: '',
    userRole: 'all',
    action: 'all',
    search: '',
  });

  // Debounced filters for API calls
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  // Check if user is org_admin
  const isOrgAdmin = user?.role === 'org_admin';

  // Fetch user pictures for the avatar display
  useEffect(() => {
    apiClient.listTenantUsers().then((data: any) => {
      const users = data?.users || data || [];
      const map: Record<string, string> = {};
      for (const u of users) {
        if (u.email && u.picture_url) {
          map[u.email] = u.picture_url;
        }
      }
      setUserPictures(map);
    }).catch(() => {});
  }, []);

  const fetchAuditEvents = React.useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await apiClient.listAuditEvents(
        currentPage,
        pageSize,
        debouncedFilters.userEmail || undefined,
        debouncedFilters.action === 'all' ? undefined : debouncedFilters.action,
        undefined, // resource - removed since not used in UI
        undefined, // method
        undefined, // startDate
        undefined, // endDate
        debouncedFilters.userRole === 'all' ? undefined : debouncedFilters.userRole
      );
      
      setEvents(response.events || []);
      setTotalPages(response.total_pages || 0);
      setTotalEvents(response.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load audit events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, debouncedFilters.userEmail, debouncedFilters.action, debouncedFilters.userRole]);


  const fetchInitialData = React.useCallback(async () => {
    try {
      setInitialLoading(true);
      setError('');
      
      // Fetch both stats and events in parallel
      const [statsResponse, eventsResponse] = await Promise.allSettled([
        apiClient.getAuditEventStats(7),
        apiClient.listAuditEvents(
          currentPage,
          pageSize,
          debouncedFilters.userEmail || undefined,
          debouncedFilters.action === 'all' ? undefined : debouncedFilters.action,
          undefined, // resource - removed since not used in UI
          undefined, // method
          undefined, // startDate
          undefined, // endDate
          debouncedFilters.userRole === 'all' ? undefined : debouncedFilters.userRole
        )
      ]);

      // Handle stats response
      if (statsResponse.status === 'fulfilled') {
        setStats(statsResponse.value);
      } else {
        console.error('Failed to load audit stats:', statsResponse.reason);
        // Set empty stats to prevent null issues
        setStats({
          total_events: 0,
          top_actions: []
        });
      }

      // Handle events response
      if (eventsResponse.status === 'fulfilled') {
        setEvents(eventsResponse.value.events || []);
        setTotalPages(eventsResponse.value.total_pages || 0);
        setTotalEvents(eventsResponse.value.total || 0);
      } else {
        setError(eventsResponse.reason?.response?.data?.message || 'Failed to load audit events');
        setEvents([]);
      }

      // Only proceed if we have both stats and events (or at least events for functionality)
      if (statsResponse.status === 'rejected' && eventsResponse.status === 'rejected') {
        setError('Failed to load audit data');
      }
    } catch (err: any) {
      setError('Failed to load audit data');
      console.error('Initial data fetch error:', err);
    } finally {
      // Only set loading to false after everything is processed
      setInitialLoading(false);
      setLoading(false);
      setHasLoadedInitialData(true);
    }
  }, [currentPage, pageSize, debouncedFilters.userEmail, debouncedFilters.action, debouncedFilters.userRole]);

  // Debounce filter changes (500ms delay for userEmail, immediate for dropdowns)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, filters.userEmail !== debouncedFilters.userEmail ? 500 : 0);

    return () => clearTimeout(timer);
  }, [filters, debouncedFilters.userEmail]);

  // Initial data load effect
  useEffect(() => {
    if (authLoading) return;

    if (!isOrgAdmin) {
      setError('You must be an organization administrator to view audit logs.');
      setInitialLoading(false);
      setLoading(false);
      return;
    }
    
    // Only use fetchInitialData if we haven't loaded initial data yet
    if (isOrgAdmin && !hasLoadedInitialData) {
      fetchInitialData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isOrgAdmin, hasLoadedInitialData]);

  // Subsequent data fetches (pagination, filtering, etc.)
  useEffect(() => {
    if (!isOrgAdmin || !hasLoadedInitialData) {
      return;
    }
    
    // For subsequent loads, only fetch events (stats don't change with pagination/filtering)
    fetchAuditEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrgAdmin, hasLoadedInitialData, currentPage, pageSize, debouncedFilters.userEmail, debouncedFilters.action, debouncedFilters.userRole]);


  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  const clearFilters = () => {
    setFilters({
      userEmail: '',
      userRole: 'all',
      action: 'all',
      search: '',
    });
    setCurrentPage(1);
  };

  const handleEventClick = (event: AuditEvent) => {
    setSelectedEvent(event);
    setIsDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsOpen(false);
    setSelectedEvent(null);
  };

  // Sanitize cell value to prevent CSV injection (formula injection) attacks
  // When opened in Excel, values starting with =, +, -, @, tab, or carriage return
  // can be interpreted as formulas and execute arbitrary commands
  const sanitizeCsvValue = (value: string): string => {
    if (!value) return '';
    // Escape double quotes by doubling them (standard CSV escaping)
    let sanitized = value.replace(/"/g, '""');
    // Prefix dangerous characters with a single quote to neutralize formulas
    // This prevents Excel from interpreting them as formulas
    if (/^[=+\-@\t\r]/.test(sanitized)) {
      sanitized = "'" + sanitized;
    }
    return sanitized;
  };

  // Export audit events to Excel
  const handleExportToExcel = async () => {
    try {
      setExporting(true);
      
      // Format date range for API
      const startDateStr = format(exportDateRange.from, 'yyyy-MM-dd');
      const endDateStr = format(exportDateRange.to, 'yyyy-MM-dd');
      
      // Fetch all events matching current filters and date range (up to 50000 with export flag)
      const response = await apiClient.listAuditEvents(
        1,
        50000, // Server allows up to 50000 for exports
        debouncedFilters.userEmail || undefined,
        debouncedFilters.action === 'all' ? undefined : debouncedFilters.action,
        undefined,
        undefined,
        startDateStr,
        endDateStr,
        debouncedFilters.userRole === 'all' ? undefined : debouncedFilters.userRole,
        true // isExport flag
      );

      const eventsToExport = response.events || [];
      
      if (eventsToExport.length === 0) {
        setError('No events found for the selected date range');
        setShowExportModal(false);
        return;
      }

      // Create CSV content (Excel compatible)
      const headers = [
        'Timestamp',
        'User Email',
        'User Role',
        'Action',
        'Resource',
        'Method',
        'Path',
        'Status Code',
        'IP Address',
        'Request ID',
        'Event ID',
        'Request Data'
      ];
      
      const csvRows = [headers.join(',')];
      
      for (const event of eventsToExport) {
        // Sanitize all user-controlled fields to prevent CSV injection
        const row = [
          `"${sanitizeCsvValue(new Date(event.timestamp).toISOString())}"`,
          `"${sanitizeCsvValue(event.user_email || '')}"`,
          `"${sanitizeCsvValue(event.user_role || '')}"`,
          `"${sanitizeCsvValue(event.action || '')}"`,
          `"${sanitizeCsvValue(event.resource || '')}"`,
          `"${sanitizeCsvValue(event.method || '')}"`,
          `"${sanitizeCsvValue(event.path || '')}"`,
          event.status_code || '',
          `"${sanitizeCsvValue(event.ip_address || '')}"`,
          `"${sanitizeCsvValue(event.request_id || '')}"`,
          `"${sanitizeCsvValue(event.id || '')}"`,
          `"${sanitizeCsvValue(event.request_data ? JSON.stringify(event.request_data) : '')}"`
        ];
        csvRows.push(row.join(','));
      }
      
      const csvContent = csvRows.join('\n');
      
      // Create blob and download
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with date range and filter info
      const filterInfo = debouncedFilters.action !== 'all' ? `_${debouncedFilters.action}` : '';
      link.download = `audit_log_${startDateStr}_to_${endDateStr}${filterInfo}.csv`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setShowExportModal(false);
      
    } catch (err: any) {
      console.error('Failed to export audit events:', err);
      setError(err.response?.data?.message || 'Failed to export audit events');
    } finally {
      setExporting(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const getActionBadgeColor = React.useCallback((action: string) => {
    // Simple 3-color system:
    // 🔴 RED = Destructive/dangerous actions or failures
    // 🟡 AMBER = Modifications/changes (updates, security actions)
    // 🟢 GREEN = Positive/additive actions

    // Check for failed actions first - always red for any failure
    if (action.endsWith('_FAILED')) {
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 hover:bg-red-100 hover:text-red-800 dark:hover:bg-red-900 dark:hover:text-red-300';
    }

    // Destructive/dangerous actions (RED)
    if (action.includes('DELETE') || action.includes('REMOVE') || action.includes('REVOKE') || 
        action.includes('OFFBOARD') || action.includes('REJECT')) {
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 hover:bg-red-100 hover:text-red-800 dark:hover:bg-red-900 dark:hover:text-red-300';
    }

    // Modifications/changes (AMBER)
    if (action.includes('UPDATE') || action.includes('RESET_PASSWORD') || action.includes('TRIGGER') ||
        action.includes('REQUEST') || action.includes('SUBMIT_POLICY_REVIEW') || action.includes('ASSESSMENT')) {
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300 hover:bg-amber-100 hover:text-amber-800 dark:hover:bg-amber-900 dark:hover:text-amber-300';
    }

    // Positive/additive actions (GREEN) - default for most actions
    // CREATE, INVITE, ONBOARD, APPROVE, ASSIGN, VIEW, etc.
    return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 hover:bg-green-100 hover:text-green-800 dark:hover:bg-green-900 dark:hover:text-green-300';
  }, []);

  const getStatusBadgeColor = React.useCallback((statusCode: number) => {
    if ((statusCode >= 200 && statusCode < 300) || statusCode === 101) {
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 hover:bg-green-100 hover:text-green-800 dark:hover:bg-green-900 dark:hover:text-green-300';
    } else if (statusCode >= 400 && statusCode < 500) {
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300 hover:bg-amber-100 hover:text-amber-800 dark:hover:bg-amber-900 dark:hover:text-amber-300';
    } else if (statusCode >= 500) {
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 hover:bg-red-100 hover:text-red-800 dark:hover:bg-red-900 dark:hover:text-red-300';
    } else {
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-gray-900 dark:hover:text-gray-300';
    }
  }, []);

  const LoadingState = () => (
    <div className="flex items-center justify-center p-8">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mr-2" />
      <span className="text-sm text-muted-foreground">Loading audit events...</span>
    </div>
  );

  const FullPageLoader = () => (
    <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center p-12 text-center">
      <FileText className="h-16 w-16 text-gray-400 mb-4" />
      <h3 className="text-lg font-semibold mb-2">No audit events found</h3>
      <p className="text-gray-600 mb-4">
        {Object.values(filters).some(v => v) 
          ? 'No events match your current filters. Try adjusting your search criteria.'
          : 'No audit events have been recorded yet.'}
      </p>
      {Object.values(filters).some(v => v) && (
        <Button variant="outline" onClick={clearFilters}>
          Clear Filters
        </Button>
      )}
    </div>
  );

  if (authLoading) {
    return <FullPageLoader />;
  }

  if (!isOrgAdmin) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium">Audit Log</CardTitle>
            <CardDescription>Security and compliance audit trail</CardDescription>
          </CardHeader>
        </Card>

        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You must be an organization administrator to view audit logs.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Show full page loader during initial data fetch or if stats is not loaded
  if (initialLoading || !stats) {
    return <FullPageLoader />;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-medium">Audit Log</CardTitle>
              <CardDescription>Security and compliance audit trail</CardDescription>
            </div>
            <Button 
              onClick={() => setShowExportModal(true)}
              disabled={totalEvents === 0}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export to Excel
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Events (7d)</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.total_events.toLocaleString()}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Top Action</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold truncate" title={stats.top_actions && stats.top_actions.length > 0 ? stats.top_actions[0].action : undefined}>
              {stats.top_actions && stats.top_actions.length > 0 
                ? stats.top_actions[0].action 
                : 'No data'}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats.top_actions && stats.top_actions.length > 0 
                ? `${stats.top_actions[0].count} events` 
                : 'No activity'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unique Actions</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.top_actions ? stats.top_actions.length : 0}
            </div>
            <p className="text-xs text-muted-foreground">
              action types
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Current Filter</CardTitle>
            <Filter className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalEvents.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">matching events</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium flex items-center gap-2">
            Filters
          </CardTitle>
          <CardDescription>
            Filter audit events by user email, role, or action type
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="userEmail">User Email</Label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="userEmail"
                  placeholder="Filter by user email..."
                  value={filters.userEmail}
                  onChange={(e) => handleFilterChange('userEmail', e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="userRole">User Role</Label>
              <Select value={filters.userRole} onValueChange={(value) => handleFilterChange('userRole', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  <SelectItem value="app_developer">App Developer</SelectItem>
                  <SelectItem value="ops_admin">Platform Admin</SelectItem>
                  <SelectItem value="org_admin">Organization Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="action">Action</Label>
              <Select value={filters.action} onValueChange={(value) => handleFilterChange('action', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All actions</SelectItem>
                  {/* Authentication */}
                  <SelectItem value="RESET_PASSWORD">RESET_PASSWORD</SelectItem>
                  {/* User Management */}
                  <SelectItem value="INVITE_USER">INVITE_USER</SelectItem>
                  <SelectItem value="REMOVE_USER">REMOVE_USER</SelectItem>
                  <SelectItem value="UPDATE_USER_ROLE">UPDATE_USER_ROLE</SelectItem>
                  <SelectItem value="REVOKE_INVITATION">REVOKE_INVITATION</SelectItem>
                  {/* Cluster Operations */}
                  <SelectItem value="ONBOARD_CLUSTER">ONBOARD_CLUSTER</SelectItem>
                  <SelectItem value="OFFBOARD_CLUSTER">OFFBOARD_CLUSTER</SelectItem>
                  {/* Policy & Risk */}
                  <SelectItem value="SUBMIT_POLICY_REVIEW">SUBMIT_POLICY_REVIEW</SelectItem>
                  <SelectItem value="UPDATE_APPROVAL_PREFERENCE">UPDATE_APPROVAL_PREFERENCE</SelectItem>
                  <SelectItem value="TRIGGER_RISK_ASSESSMENT">TRIGGER_RISK_ASSESSMENT</SelectItem>
                  <SelectItem value="VALIDATE_RISK_ASSESSMENT_FIXES">VALIDATE_RISK_ASSESSMENT_FIXES</SelectItem>
                  {/* Namespace Operations */}
                  <SelectItem value="ASSIGN_NAMESPACE_OWNER">ASSIGN_NAMESPACE_OWNER</SelectItem>
                  <SelectItem value="APPROVE_OWNERSHIP_REQUEST">APPROVE_OWNERSHIP_REQUEST</SelectItem>
                  <SelectItem value="REJECT_OWNERSHIP_REQUEST">REJECT_OWNERSHIP_REQUEST</SelectItem>

                  {/* Chat */}
                  <SelectItem value="STORE_CHAT_YAML_CHANGE">STORE_CHAT_YAML_CHANGE</SelectItem>
                  <SelectItem value="APPROVE_CHAT_YAML_CHANGE">APPROVE_CHAT_YAML_CHANGE</SelectItem>
                  <SelectItem value="APPLY_CHAT_YAML_CHANGE">APPLY_CHAT_YAML_CHANGE</SelectItem>
                  <SelectItem value="UPDATE_CLUSTER_SAFE_APPLY">UPDATE_CLUSTER_SAFE_APPLY</SelectItem>

                  {/* Network Policy Simulation */}
                  <SelectItem value="CREATE_NETWORK_POLICY_SIMULATION">CREATE_NETWORK_POLICY_SIMULATION</SelectItem>
                  <SelectItem value="DELETE_NETWORK_POLICY_SIMULATION">DELETE_NETWORK_POLICY_SIMULATION</SelectItem>

                  {/* Authorization Policy Simulation */}
                  <SelectItem value="CREATE_AUTHORIZATION_POLICY_SIMULATION">CREATE_AUTHORIZATION_POLICY_SIMULATION</SelectItem>
                  <SelectItem value="DELETE_AUTHORIZATION_POLICY_SIMULATION">DELETE_AUTHORIZATION_POLICY_SIMULATION</SelectItem>

                  {/* Kubernetes Incident Response */}
                  <SelectItem value="APPLY_INCIDENT_FIX">APPLY_INCIDENT_FIX</SelectItem>
                  <SelectItem value="VALIDATE_INCIDENT_FIX">VALIDATE_INCIDENT_FIX</SelectItem>
                  <SelectItem value="MARK_INCIDENT_RESOLVED">MARK_INCIDENT_RESOLVED</SelectItem>
                  <SelectItem value="GENERATE_INCIDENT_POSTMORTEM">GENERATE_INCIDENT_POSTMORTEM</SelectItem>
                  <SelectItem value="UPDATE_POSTMORTEM_IMPACT_ASSESSMENT">UPDATE_POSTMORTEM_IMPACT_ASSESSMENT</SelectItem>

                  {/* Cloud Incident Response */}
                  <SelectItem value="APPLY_CLOUD_INCIDENT_FIX">APPLY_CLOUD_INCIDENT_FIX</SelectItem>
                  <SelectItem value="ROLLBACK_CLOUD_INCIDENT_FIX">ROLLBACK_CLOUD_INCIDENT_FIX</SelectItem>
                  <SelectItem value="VALIDATE_CLOUD_INCIDENT_FIX">VALIDATE_CLOUD_INCIDENT_FIX</SelectItem>
                  <SelectItem value="MARK_CLOUD_INCIDENT_RESOLVED">MARK_CLOUD_INCIDENT_RESOLVED</SelectItem>
                  <SelectItem value="TRIGGER_CLOUD_INCIDENT_RCA">TRIGGER_CLOUD_INCIDENT_RCA</SelectItem>

                  {/* Chat Cloud Changes */}
                  <SelectItem value="STORE_CHAT_CLOUD_CHANGE">STORE_CHAT_CLOUD_CHANGE</SelectItem>
                  <SelectItem value="APPLY_CHAT_CLOUD_CHANGE">APPLY_CHAT_CLOUD_CHANGE</SelectItem>
                  <SelectItem value="CREATE_CHAT_CLOUD_CHANGE_PR">CREATE_CHAT_CLOUD_CHANGE_PR</SelectItem>

                  {/* IaC Repository Operations */}
                  <SelectItem value="ENABLE_IAC_REPO">ENABLE_IAC_REPO</SelectItem>
                  <SelectItem value="DISABLE_IAC_REPO">DISABLE_IAC_REPO</SelectItem>
                  <SelectItem value="TRIGGER_IAC_ANALYSIS">TRIGGER_IAC_ANALYSIS</SelectItem>

                  {/* GitHub Integration */}
                  <SelectItem value="CONNECT_GITHUB_INTEGRATION">CONNECT_GITHUB_INTEGRATION</SelectItem>
                  <SelectItem value="DISCOVER_DEPLOYABLE_APPLICATIONS">DISCOVER_DEPLOYABLE_APPLICATIONS</SelectItem>
                  <SelectItem value="START_DEPLOYMENT_PREPARATION">START_DEPLOYMENT_PREPARATION</SelectItem>
                  <SelectItem value="CREATE_DEPLOYMENT_PULL_REQUEST">CREATE_DEPLOYMENT_PULL_REQUEST</SelectItem>
                  <SelectItem value="CREATE_GITHUB_PULL_REQUEST">CREATE_GITHUB_PULL_REQUEST</SelectItem>

                  {/* SSO Configuration */}
                  <SelectItem value="CREATE_OIDC_CONFIG">CREATE_OIDC_CONFIG</SelectItem>
                  <SelectItem value="UPDATE_OIDC_CONFIG">UPDATE_OIDC_CONFIG</SelectItem>
                  <SelectItem value="DELETE_OIDC_CONFIG">DELETE_OIDC_CONFIG</SelectItem>
                  <SelectItem value="CREATE_SCIM_CONFIG">CREATE_SCIM_CONFIG</SelectItem>
                  <SelectItem value="UPDATE_SCIM_CONFIG">UPDATE_SCIM_CONFIG</SelectItem>
                  <SelectItem value="DELETE_SCIM_CONFIG">DELETE_SCIM_CONFIG</SelectItem>

                  {/* Slack Integration */}
                  <SelectItem value="GET_SLACK_INSTALL_URL">GET_SLACK_INSTALL_URL</SelectItem>
                  <SelectItem value="COMPLETE_SLACK_OAUTH">COMPLETE_SLACK_OAUTH</SelectItem>
                  <SelectItem value="DISCONNECT_SLACK_INTEGRATION">DISCONNECT_SLACK_INTEGRATION</SelectItem>
                  <SelectItem value="SYNC_SLACK_USERS">SYNC_SLACK_USERS</SelectItem>

                  {/* AWS Integration */}
                  <SelectItem value="BOOTSTRAP_AWS_INTEGRATION">BOOTSTRAP_AWS_INTEGRATION</SelectItem>
                  <SelectItem value="VERIFY_AWS_INTEGRATION">VERIFY_AWS_INTEGRATION</SelectItem>
                  <SelectItem value="DISCONNECT_AWS_INTEGRATION">DISCONNECT_AWS_INTEGRATION</SelectItem>

                  {/* OCI Integration */}
                  <SelectItem value="CONNECT_OCI_TENANCY">CONNECT_OCI_TENANCY</SelectItem>
                  <SelectItem value="DISCONNECT_OCI_TENANCY">DISCONNECT_OCI_TENANCY</SelectItem>

                  {/* EKS Cluster Operations */}
                  <SelectItem value="CREATE_EKS_CLUSTER_LINK">CREATE_EKS_CLUSTER_LINK</SelectItem>
                  <SelectItem value="UPDATE_EKS_CLUSTER">UPDATE_EKS_CLUSTER</SelectItem>
                  <SelectItem value="DELETE_EKS_CLUSTER_LINK">DELETE_EKS_CLUSTER_LINK</SelectItem>

                  {/* Billing */}
                  <SelectItem value="CREATE_BILLING_CHECKOUT_SESSION">CREATE_BILLING_CHECKOUT_SESSION</SelectItem>
                  <SelectItem value="CREATE_BILLING_PORTAL_SESSION">CREATE_BILLING_PORTAL_SESSION</SelectItem>

                  {/* Cloud Fix Placeholders */}
                  <SelectItem value="UPDATE_CLOUD_FIX_PLACEHOLDERS">UPDATE_CLOUD_FIX_PLACEHOLDERS</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end gap-2">
              <Button variant="outline" onClick={clearFilters} className="w-full">
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Audit Events Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-medium">Audit Events</CardTitle>
              <CardDescription>
                {totalEvents > 0 && `${totalEvents.toLocaleString()} total events`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="pageSize">Show</Label>
              <Select value={pageSize.toString()} onValueChange={(value) => setPageSize(Number(value))}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <LoadingState />
          ) : events.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>IP Address</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((event) => (
                    <TableRow 
                      key={event.id} 
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                      onClick={() => handleEventClick(event)}
                    >
                      <TableCell className="font-mono text-sm">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          {formatTimestamp(event.timestamp)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {userPictures[event.user_email] ? (
                            <img src={userPictures[event.user_email]} alt="" className="h-6 w-6 rounded-full" />
                          ) : (
                            <User className="h-5 w-5 text-gray-400" />
                          )}
                          <div className="font-medium">{event.user_email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-sm">
                          {event.user_role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getActionBadgeColor(event.action)}>
                          {event.action}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadgeColor(event.status_code)}>
                          {event.status_code}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{event.ip_address}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between pt-4">
                <div className="text-sm text-gray-600">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalEvents)} of {totalEvents.toLocaleString()} events
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Audit Event Details Popup */}
      <AuditEventDetails
        event={selectedEvent}
        isOpen={isDetailsOpen}
        onClose={handleCloseDetails}
      />

      {/* Export Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Export Audit Log</DialogTitle>
            <DialogDescription>
              Select a date range to export audit events. The export will include up to 50,000 events.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label className="text-sm font-medium mb-2 block">Date Range</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !exportDateRange && "text-muted-foreground"
                  )}
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {exportDateRange?.from && exportDateRange?.to ? (
                    <>
                      {format(exportDateRange.from, 'MMM d, yyyy')} - {format(exportDateRange.to, 'MMM d, yyyy')}
                    </>
                  ) : (
                    <span>Pick a date range</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  initialFocus
                  mode="range"
                  defaultMonth={exportDateRange?.from}
                  selected={{ from: exportDateRange?.from, to: exportDateRange?.to }}
                  onSelect={(range: any) => {
                    if (range?.from && range?.to) {
                      const from = new Date(range.from);
                      from.setHours(0, 0, 0, 0);
                      const to = new Date(range.to);
                      to.setHours(23, 59, 59, 999);
                      setExportDateRange({ from, to });
                    }
                  }}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
            
            {debouncedFilters.action !== 'all' && (
              <p className="mt-3 text-sm text-muted-foreground">
                <strong>Note:</strong> Exporting events filtered by action: <Badge variant="outline">{debouncedFilters.action}</Badge>
              </p>
            )}
            {debouncedFilters.userEmail && (
              <p className="mt-2 text-sm text-muted-foreground">
                <strong>Note:</strong> Exporting events filtered by user: <Badge variant="outline">{debouncedFilters.userEmail}</Badge>
              </p>
            )}
            {debouncedFilters.userRole !== 'all' && (
              <p className="mt-2 text-sm text-muted-foreground">
                <strong>Note:</strong> Exporting events filtered by role: <Badge variant="outline">{debouncedFilters.userRole}</Badge>
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleExportToExcel}
              disabled={exporting || !exportDateRange?.from || !exportDateRange?.to}
            >
              {exporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Export to Excel
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLog;